import React, { PropTypes } from 'react';
import { connect } from 'react-redux';
import { map, compose, reduce, filter, median } from 'ramda';
import jsPDF from 'jspdf';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { autoTable } from 'jspdf-autotable';

// import Stats from './Stats';

const PrintCatalog = ({ horses, sale, saleId, previousSaleStats }) => {
    const downloadPrintCatalog = () => {
        const doc = new jsPDF('l', 'pt', 'letter'); // eslint-disable-line new-cap
        const columns = [
            { title: 'HIP', dataKey: 'hip' },
            { title: '', dataKey: 'sex' },
            { title: 'NAME', dataKey: 'name' },
            { title: 'SIRE', dataKey: 'sire' },
            { title: 'DAM', dataKey: 'dam' },
            { title: 'CONSIGNOR', dataKey: 'consignor' },
            { title: 'BUYER', dataKey: 'buyer' },
            { title: 'PRICE', dataKey: 'price' },
        ];

        const priceDisplay = (horse) => {
            if (sale.show_outs && (horse.purchaser === 'OUT' || horse.out)) {
                return 'OUT';
            } else if ( horse.price !== null) {
                let price = horse.purchaser == 'NOT SOLD' ? `($${horse.price.toLocaleString()})` : `$${horse.price.toLocaleString()}`;
                price = horse.private_sale ? `${price} PS` : price;
                return price;
            } else {
                return '';
            }
        }

        const rows = map((horse) => {
            const row = {
                hip: horse.hip,
                sex: horse.sex,
                name: horse.name,
                sire: horse.sire,
                dam: horse.dam,
                consignor: horse.consignor,
                buyer: horse.purchaser !== null ? horse.purchaser : "",
                price: priceDisplay(horse),
            };
            return map((i) => {
                if (typeof i === 'string') {
                    return i.toUpperCase();
                }
                return i;
            })(row);
        })(horses);

        doc.autoTable(columns, rows, {
            theme: 'striped',
            styles: {
                cellPadding: { top: 7, left: 3, right: 1, bottom: 7 },
                cellWidth: 'wrap',
                overflow: 'linebreak',
                fontSize: 8,
                font: 'helvetica',
                lineColor: 200,
                lineWidth: 1,
                fontStyle: 'normal',
                textColor: 20,
                valign: 'middle',
            },
            headerStyles: {
                fontStyle: 'bold',
                textColor: 255,
            },
            margin: { top: 80, left: 20, right: 20, bottom: 40 },
            addPageContent: () => {
                doc.setFont('helvetica');
                doc.setFontType('bold');
                doc.text('FASIG-TIPTON', 20, 50);
                doc.text(global.drupalSettings.catalogues.sale_name, 20, 70);
                doc.setFontType('normal');
                doc.setFontSize(10);
            },
            columnStyles: {
                hip: { columnWidth: 25 },
                sex: { columnWidth: 15 },
                consignor: { columnWidth: 150 },
                buyer: { columnWidth: 100 },
                price: { columnWidth: 60 },
            },
        });

        // STATS TABLE AT BOTTOM

        const filterOuts = filter((horse) => {
            return !(horse.out || horse.purchaser === 'OUT');
        });


        const filteredHorses = filterOuts(horses);
        const soldHorses = filter(horse => horse.price && !(horse.purchaser === 'NOT SOLD'))(filteredHorses);
        const notSoldHorses = filter(horse => horse.price !== 0 && horse.purchaser === 'NOT SOLD')(filteredHorses);
        const outsHorses = filter(horse => horse.out || horse.purchaser === 'OUT')(horses);
        const totalPrice = reduce((acc, horse) => {
            return horse.price + acc;
        }, 0);

        const average = Math.round(totalPrice(soldHorses) / soldHorses.length);
        const medianPrice = compose(
            median,
            map(horse => horse.price),
        );


        const uniqueDaysOfSale = [...new Set(map(horse => horse.session)(filteredHorses))];
        const dayRows = map((day) => {
            const filteredHorsesByDay = filter(horse => horse.session === day)(filteredHorses);
            const soldHorsesByDay = filter(horse => horse.price && !(horse.purchaser === 'NOT SOLD'))(filteredHorsesByDay);
            const notSoldHorsesByDay = filter(horse => horse.price && horse.purchaser === 'NOT SOLD')(filteredHorsesByDay);
            const allHorsesByDay = filter(horse => horse.session === day)(horses);
            const horsesThroughRingOnDay = compose(
                map(horse => horse.hip),
                filter(horse => (horse.purchaser !== "NO STATUS"|| horse.price || horse.out)),
                filter(horse => horse.session === day),
              )(horses);

            const dailyTotalPrice = totalPrice(soldHorsesByDay);
            const dailyAverage = Math.round(dailyTotalPrice / soldHorsesByDay.length);
            const dailyMedianPrice = medianPrice(soldHorsesByDay);
            const dailyStatus = (
                allHorsesByDay.length === horsesThroughRingOnDay.length && 'Completed'  ||
                moment().isBetween(day, moment(day).add(1, 'd')) && 'In Progress' ||
                moment().isBefore(day) && 'Not Started'
                );
            return ([
                moment(day).format('MM/DD/YYYY'),
                soldHorsesByDay.length,
                `$${dailyTotalPrice.toLocaleString('en-US')}`,
                isNaN(dailyAverage) ? 'N/A' : `$${dailyAverage.toLocaleString('en-US')}`,
                notSoldHorsesByDay.length,
                isNaN(dailyMedianPrice) ? 'N/A' : `$${dailyMedianPrice.toLocaleString('en-US')}`,
                dailyStatus,
            ]);
        })(uniqueDaysOfSale.sort());

        const horsesThroughRingForSale = compose(
            map(horse => horse.hip),
            filter(horse => (horse.purchaser !== "NO STATUS" || horse.price || horse.out)),
          )(horses);


        const statsColumns = ['', 'Sold', 'Total', 'Average', 'Not Sold', 'Median', 'Status'];
        const statsRows = [...dayRows, [
            'TOTALS',
            soldHorses.length,
            `$${totalPrice(soldHorses).toLocaleString()}`,
            isNaN(average) ? 'N/A' : `$${average.toLocaleString()}`,
            notSoldHorses.length,
            isNaN(medianPrice(soldHorses)) ? 'N/A' : `$${medianPrice(soldHorses).toLocaleString()}`,
            horses.length === horsesThroughRingForSale.length  && 'Completed' ||
            moment().isBetween(uniqueDaysOfSale[0], moment(uniqueDaysOfSale[uniqueDaysOfSale.length - 1]).add(1, 'd')) && 'In Progress' ||
            moment().isBefore(uniqueDaysOfSale[0]) && 'Not Started',
        ]];

        const first = doc.autoTable.previous;

        doc.autoTable(statsColumns, statsRows, {
            startY: first.finalY + 30,
            margin: { bottom: 30 },
            pageBreak: 'avoid',
        });

        // Previous Sale Stats
        const selectedSale = previousSaleStats[saleId] ? previousSaleStats[saleId] : {};

        const {
            daily_stats = [],
            sale_sold = 0,
            sale_total_price = 0,
            sale_average = 0,
            sale_not_sold = 0,
            sale_median = 0,
        } = selectedSale;

        const previousStatsColumns = ['', 'Sold', 'Total', 'Average', 'Not Sold', 'Median'];
        const previousSaleDayRows = map((day) => {
            return ([
                    moment(day.day).format('MM/DD/YYYY'),
                    day.sold,
                    day.total.toLocaleString('en-US'),
                    day.average.toLocaleString('en-US'),
                    day.not_sold,
                    day.median.toLocaleString('en-US'),
            ]);
        })(daily_stats);

        const previousStatsRows = [...previousSaleDayRows, [
            'TOTALS',
            sale_sold,
            sale_total_price.toLocaleString('en-US'),
            sale_average.toLocaleString('en-US'),
            sale_not_sold,
            sale_median.toLocaleString('en-US'),
        ]];

        const second = doc.autoTable.previous;

        // doc.autoTable(previousStatsColumns, previousStatsRows, {
        //     startY: second.finalY + 30,
        //     margin: { bottom: 30 },
        //     pageBreak: 'avoid',
        // });

        let isIE = /*@cc_on!@*/false || !!document.documentMode;
        let isEdge = /*@cc_on!@*/false || /Edge/.test(navigator.userAgent);
        let isIOSChrome = navigator.userAgent.match('CriOS');

        if (isIOSChrome) {
            let output = doc.output('datauristring');
            let w = window.open("", "MsgWindow");
            w.document.write("<html><head><title>" + global.drupalSettings.catalogues.sale_name + "</title></head><body><iframe src=" + output + " style='overflow: auto; -webkit-overflow-scrolling: touch; border: none;' scrolling='yes' width='100%' height='5000%'></iframe></body></html>");
            alert('This may not work properly for Chrome on iOS. Try using a different browser.');
        } else if (!isIE && !isEdge) {
            window.open(doc.output('bloburl'));
        } else {
            doc.save(`${global.drupalSettings.catalogues.sale_name}.pdf`);
        }
    };


    return (
        <button className="btn btn-link" onClick={() => downloadPrintCatalog()}>
            <FontAwesomeIcon style={{verticalAlign:"middle" }} className="control-button" icon={['fas', 'print']} />
        </button>
    );
};

const mapState = (state) => {
    return {
        previousSaleStats: state.previousSaleStats,
    };
};

export default connect(mapState)(PrintCatalog);
